import { useCallback, useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { EmptyState } from "@/core/ui";
import useMatchesUser from "./hooks/useMatchesUser";

const MatchesUser = () => {
  const {
    initialize,
    playerId,
    rankingMatchesData,
    hasMultipleRankings,
    isWithoutSeason,
    loading,
  } = useMatchesUser();
  const [expandedRanking, setExpandedRanking] = useState(null);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (rankingMatchesData.length > 0 && !expandedRanking) {
      setExpandedRanking(rankingMatchesData[0].id);
    }
  }, [expandedRanking, rankingMatchesData]);

  const isLoading = loading?.rankings || loading?.matches;

  const isUserWin = useCallback(
    (match) => {
      if (!match.score || !playerId) return false;

      const isOnTeamA = match.teamAPlayerIds?.includes(playerId);
      const isOnTeamB = match.teamBPlayerIds?.includes(playerId);

      if (isOnTeamA) {
        return match.score.teamA > match.score.teamB;
      }

      if (isOnTeamB) {
        return match.score.teamB > match.score.teamA;
      }

      return false;
    },
    [playerId],
  );
  const handleExpand = useCallback(
    (rankingId) => (_, isExpanded) => {
      setExpandedRanking(isExpanded ? rankingId : false);
    },
    [],
  );

  const renderRound = useCallback(
    (round) => {
      const winsInRound = round.matches.filter(
        (match) => match.score && isUserWin(match),
      ).length;
      const isKingOfCourt =
        round.matches.length > 0 && winsInRound === round.matches.length;

      return (
        <Box key={round.id}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {round.title}
              </Typography>
              {/* {round.status === "ACTIVE" ? (
              <Chip
                label="Em andamento"
                color="info"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            ) : null} */}
            </Stack>
            {isKingOfCourt ? (
              <Chip
                label="Rei da Quadra"
                color="warning"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            ) : null}
          </Stack>

          <Stack spacing={1}>
            {round.matches.map((match) => {
              const won = match.score ? isUserWin(match) : false;
              const pending = !match.score;

              return (
                <Paper
                  key={match.id}
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: pending
                      ? "divider"
                      : won
                        ? "success.main"
                        : "error.main",
                    backgroundColor: pending
                      ? "background.paper"
                      : won
                        ? "rgba(46, 125, 50, 0.08)"
                        : "rgba(211, 47, 47, 0.08)",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        noWrap
                        title={match.teamA[0]}
                        sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {match.teamA[0]}
                      </Typography>
                      <Typography
                        noWrap
                        title={match.teamA[1]}
                        sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {match.teamA[1]}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: { xs: 30, sm: 38 },
                        fontWeight: 800,
                        lineHeight: 1,
                        color: pending ? "text.secondary" : "text.primary",
                      }}
                    >
                      {match.score
                        ? `${match.score.teamA} x ${match.score.teamB}`
                        : "X"}
                    </Typography>

                    <Box sx={{ textAlign: "right", minWidth: 0 }}>
                      <Typography
                        noWrap
                        title={match.teamB[0]}
                        sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {match.teamB[0]}
                      </Typography>
                      <Typography
                        noWrap
                        title={match.teamB[1]}
                        sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {match.teamB[1]}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      );
    },
    [isUserWin],
  );

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <Stack spacing={2}>
          {[1, 2].map((item) => (
            <Box key={`matches-skeleton-${item}`}>
              <Skeleton variant="text" width={180} height={34} />
              <Skeleton variant="text" width={120} height={24} sx={{ mb: 1 }} />
              <Stack spacing={1}>
                {[1, 2, 3].map((match) => (
                  <Skeleton
                    key={`match-row-${item}-${match}`}
                    variant="rounded"
                    height={74}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      );
    }

    if (isWithoutSeason) {
      return (
        <EmptyState
          icon={<SportsBaseballIcon sx={{ fontSize: 34 }} />}
          title="Nenhum ranking encontrado"
          description="Assim que você entrar em um ranking, suas rodadas e partidas aparecerão aqui. Se necessário, fale com a administração."
        />
      );
    }

    if (!hasMultipleRankings) {
      return (
        <Stack spacing={2}>
          {(rankingMatchesData[0]?.rounds || []).map(renderRound)}
        </Stack>
      );
    }

    return (
      <Stack spacing={1.25}>
        {rankingMatchesData.map((ranking) => (
          <Accordion
            key={ranking.id}
            expanded={expandedRanking === ranking.id}
            onChange={handleExpand(ranking.id)}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                py: 1,
                background:
                  expandedRanking === ranking.id
                    ? "linear-gradient(180deg, rgba(25,118,210,0.10) 0%, rgba(25,118,210,0.03) 100%)"
                    : "transparent",
                "& .MuiAccordionSummary-content": { my: 0 },
              }}
            >
              <Stack spacing={0.35}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {ranking.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ranking.rounds.length} rodada(s)
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2, pt: 1.5 }}>
              <Stack spacing={2}>{ranking.rounds.map(renderRound)}</Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    );
  }, [
    expandedRanking,
    handleExpand,
    hasMultipleRankings,
    isLoading,
    isWithoutSeason,
    rankingMatchesData,
    renderRound,
  ]);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Partidas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Acompanhe suas rodadas e resultados por temporada
      </Typography>
      {content}
    </Paper>
  );
};

export default MatchesUser;
